import React, { useState, useRef, useEffect } from 'react';
import Config from '../../config';
import { Button, Grid, Card, Typography, Stepper, Step, StepLabel, StepContent, Dialog, DialogContent, Toolbar, DialogActions } from '@mui/material';
import ExtendedField from '../../components/text_field';
import FastSelect from '../../components/fast_suggest_field';
import DownloadButton from '../../components/download_button';
import Helpers from '../../helpers';
import axios from 'axios';
import { Redirect } from 'react-router-dom';
import DocumentLink from '../../components/document_link';
import Moment from 'react-moment';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import Helper from '../../helpers';
import ContentLoader from '../../components/content_loader';

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (same pattern as
// inventory/v1/categories.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;

type FormFieldDescriptor = {
    name: string;
    label: string;
    options?: string[];
};

type FormSection = {
    section: string;
    fields: FormFieldDescriptor[];
};

type TestCaseDocument = {
    Id?: number | string;
    DocId?: number;
    DocumentType: string;
    Passed?: boolean;
    ErrorMessage?: string;
    Source?: string;
};

type TestCaseItem = {
    TestCaseId: number;
    TestName: string;
    Description?: string;
    PassedDocuments: number;
    TotalDocuments: number;
    PassedConditionally?: boolean;
    OrderId?: number | null;
    PartnerPo?: string;
    StartDate?: string;
    ProgressId?: number;
    documents?: TestCaseDocument[];
};

type TestCaseProps = {
    item: TestCaseItem;
    partnerId?: string;
    disabled?: boolean;
    embedded?: boolean;
    onGenerate: (orderId?: number | null) => void;
};

// `testCaseId` is never read during render, so it lives in a ref rather than
// state to keep it synchronously current across the async generate flow.
type TestCaseState = {
    showError: boolean;
    showEdit: boolean;
    error?: string;
    disableEdit?: boolean;
    order?: any;
    fields?: FormSection[];
};

const TestCase = (props: TestCaseProps) => {
    const { item, partnerId, disabled, embedded, onGenerate } = props;
    const [state, setState] = useState<TestCaseState>({ showError: false, showEdit: false });
    const merge = (partial: Partial<TestCaseState>) => setState(prev => ({ ...prev, ...partial }));
    const testCaseIdRef = useRef<number | undefined>(undefined);

    const generateOrder = async (values: any) => {
        merge({ disableEdit: true });
        let data = { TestCaseId: testCaseIdRef.current, Order: JSON.stringify(values), PartnerId: partnerId };
        try {
            let error = (await axios.post(Config.api + "/odata/Company/Functions.GenerateTestOrder", data)).data;
            if (error) {
                merge({ error: error, showError: true, disableEdit: false });
            } else {
                merge({ showEdit: false, disableEdit: false });
                let orderId = null;
                if (embedded) {
                    orderId = (await axios.get(Config.api + `/odata/Company/VwTestCaseDetails?$filter=TestCaseId eq ${testCaseIdRef.current} and PartnerCoId eq ${partnerId}`)).data.value[0].OrderId;
                }
                onGenerate(orderId);
            }
        } catch (e) {
            merge({ error: "Failed to generate test order.", showError: true, disableEdit: false });
        }
    };
    const getOrder = async (testCaseId: number) => {
        try {
            let url = Config.api + "/odata/Company/Functions.GetTestCaseOrder?partnerId=" + partnerId + "&testcaseid=" + testCaseId;
            let order = JSON.parse((await axios.get(url)).data.value);
            let fields = getOrderFormFields(order);
            if (fields.length) {
                testCaseIdRef.current = testCaseId;
                merge({ showEdit: true, disableEdit: false, order, fields });
            } else {
                testCaseIdRef.current = testCaseId;
                await generateOrder(order);
            }
        } catch (e: any) {
            let error = 'An unexpected error occurred.';
            if (e.response && e.response.data) {
                error = new Helper().getApiErrors(e.response.data).join("\n")
            }
            merge({ showError: true, error: error });
        }
    };
    const getLabel = (str: string) => {
        return str.replace(/(^[a-z]+)|[0-9]+|[A-Z][a-z]+|[A-Z]+(?=[A-Z][a-z]|[0-9])/g,
            function (match, first) {
                if (first) {
                    match = match[0].toUpperCase() + match.substr(1);
                }
                return match = match + ' ';
            }).trim();
    };
    const getFormFields = (obj: any, prefix?: string): FormFieldDescriptor[] => {
        let ret: FormFieldDescriptor[] = [];
        if (!obj) {
            return ret;
        }
        prefix = prefix || "";
        let keys = Object.keys(obj);
        for (let i = 0; i < keys.length; i++) {
            let fieldName = prefix + keys[i];
            let val = obj[keys[i]];
            if (typeof (val) === "string") {
                if (val === "*") {
                    ret.push({ name: fieldName, label: getLabel(keys[i]) });
                    obj[keys[i]] = "";
                }
                else if (val.endsWith("*")) {
                    ret.push({ name: fieldName, label: getLabel(keys[i]) });
                    obj[keys[i]] = val.substring(0, val.length - 1);
                }
                else if (val.indexOf("|") > -1) {
                    let options = val.split('|').map((r: string) => r.trim()).filter((r: string) => r !== null && r !== "");
                    obj[keys[i]] = options.length > 0 ? options[0] : '';
                    ret.push({ name: fieldName, label: getLabel(keys[i]), options });
                }
            }
        }
        return ret;
    };
    const getKvpFormFields = (kvps: any[], prefix?: string): FormFieldDescriptor[] => {
        let ret: FormFieldDescriptor[] = [];
        if (!kvps || kvps.length === 0) {
            return ret;
        }
        prefix = prefix || "";
        for (let i = 0; i < kvps.length; i++) {
            let fieldName = prefix + "[" + i + "].Value";
            let kvp = kvps[i] || {};
            if (!kvp.Name) {
                continue;
            }
            let val = kvp.Value;
            if (typeof (val) === "string") {
                if (val === "*") {
                    ret.push({ name: fieldName, label: kvp.Name });
                    kvp.Value = "";
                }
                else if (val.endsWith("*")) {
                    ret.push({ name: fieldName, label: kvp.Name });
                    kvp.Value = val.substring(0, val.length - 1);
                }
                else if (val.indexOf("|") > -1) {
                    let options = val.split('|').map((r: string) => r.trim()).filter((r: string) => r !== null && r !== "");
                    kvp.Value = options.length > 0 ? options[0] : '';
                    ret.push({ name: fieldName, label: kvp.Name, options });
                }
            }
        }
        return ret;
    };
    const getOrderFormFields = (order: any): FormSection[] => {
        let keys = Object.keys(order);
        let fields: FormSection[] = [];
        let toplevelFields = getFormFields(order).concat(getKvpFormFields(order.ExtendedAttributes, "ExtendedAttributes"));
        if (toplevelFields.length > 0) {
            fields.push({ section: "Header", fields: toplevelFields });
        }
        for (let i = 0; i < keys.length; i++) {
            if (keys[i].endsWith('Address')) {
                let addressFields = getFormFields(order[keys[i]], keys[i] + ".");
                if (addressFields.length > 0) {
                    fields.push({ section: getLabel(keys[i]), fields: addressFields });
                }
            }
        }
        if (order.ShipmentInfos && order.ShipmentInfos[0]) {
            let shippingInfo = getFormFields(order.ShipmentInfos[0], "ShipmentInfos[0].");
            if (shippingInfo.length > 0) {
                fields.push({ section: "Shipping Info", fields: shippingInfo });
            }
        }
        for (let i = 0; i < order.OrderLines.length; i++) {
            let topLevel = getFormFields(order.OrderLines[i], `OrderLines[${i}].`);
            let identifier = getFormFields(order.OrderLines[i].ItemIdentifier, `OrderLines[${i}].ItemIdentifier.`);
            let itemFields = topLevel.concat(identifier).concat(getKvpFormFields(order.OrderLines[i].ExtendedAttributes, `OrderLines[${i}].ExtendedAttributes`));
            if (itemFields.length > 0) {
                fields.push({ section: `Item #${i + 1}`, fields: itemFields });
            }
        }
        return fields;
    };
    const renderField = (name: string, label: string, options?: string[]) => {
        let component = ExtendedField;
        if (options && options.length > 0) {
            component = FastSelect as any;
        }
        return <Field disabled={state.disableEdit} style={{ flex: 1 }} key={name} label={label} name={name} component={component} isClearable options={(options || []).map(r => ({ label: r, value: r }))} />;
    };

    let orderStep: React.ReactNode = "Create a test order";
    let status = 'In Progress';
    let color = '#ffa500';
    if (item.PassedConditionally && item.TotalDocuments === 0) {
        status = 'Not Required';
        color = "#448044";
    } else if (item.PassedDocuments === item.TotalDocuments && item.TotalDocuments > 0) {
        status = 'Complete';
        color = '#448044';
    } else if (item.PassedDocuments !== item.TotalDocuments && !item.OrderId) {
        status = 'Not Started';
        color = "#ffa500";
    }
    let statusStyle: React.CSSProperties = {
        backgroundColor: color,
        color: color ? 'white' : undefined,
        lineHeight: '3em',
        textAlign: 'center'
    };
    if (item.OrderId) {
        orderStep = <span>{'Order '}<DocumentLink type='order' id={item.OrderId}>{item.PartnerPo}</DocumentLink>{' created on '}<Moment format='MM/DD/YY [at] h:mm A'>{item.StartDate}</Moment>{' ('}<DocumentLink type='order' id={item.OrderId}>#{item.OrderId}</DocumentLink>{')'}</span>;
    }
    return (
        <>
            <Grid container>
                <Grid size={{ md: 6 }}>
                    <Typography variant="h6" component="h2" style={{ textAlign: "center", padding: '0 1em 0 1em', lineHeight: '3em', textOverflow: embedded ? 'ellipsis' : undefined, overflow: 'hidden', whiteSpace: embedded ? 'nowrap' : undefined }}>{item.TestName}</Typography>
                </Grid>
                <Grid size={{ md: 6 }}>
                    <Typography style={statusStyle} variant="h6" component="h2">{status}</Typography>
                </Grid>
                <Grid container style={{ width: '100%' }}>
                    <Grid size={{ md: 6 }} style={{ paddingLeft: '1em', overflow: 'auto', height: embedded ? "500px" : undefined }}>
                        <div style={{ textAlign: 'left', fontSize: '0.875rem', padding: '0.5em 1em 1em 1em' }} dangerouslySetInnerHTML={{ __html: item.Description || '' }}></div>
                    </Grid>
                    <Grid size={{ md: 6 }} style={{ overflow: 'auto', height: embedded ? "500px" : undefined }}>
                        <div>
                            <div className='noPrint' style={{ textAlign: 'left', marginTop: '0.5em' }}>
                                <DownloadButton variant='contained' color='primary' disabled={disabled} onClick={() => getOrder(item.TestCaseId)}>Create a New Test Order</DownloadButton>
                            </div>
                            <div style={{ textAlign: 'left', padding: '0.5em 1em 1em 1em' }}>
                                {(!item.PassedConditionally || item.TotalDocuments > 0) ?
                                    <Stepper orientation="vertical" nonLinear={true}>
                                        <Step key='order' completed={(item.OrderId ?? 0) > 0} active={true}><StepLabel>{orderStep}</StepLabel></Step>
                                        {item.documents && item.documents.map(doc => {
                                            let label: React.ReactNode = 'Create a' + (['a', 'e', 'i', 'o', 'u'].indexOf(doc.DocumentType.toLowerCase().substring(0, 1)) > -1 ? 'n' : '') + ' ' + doc.DocumentType.toLowerCase();
                                            if (doc.DocId) {
                                                label = <span>{doc.DocumentType + ' passed ('}<DocumentLink id={doc.DocId} type={doc.DocumentType}>#{doc.DocId}</DocumentLink>{` from ${doc.Source})`}</span>;
                                                if (!doc.Passed) {
                                                    label = <span>{doc.DocumentType + ' received but there was an error ('}<DocumentLink id={doc.DocId} type={doc.DocumentType}>#{doc.DocId}</DocumentLink>{` from ${doc.Source})`}</span>;
                                                }
                                            }
                                            return <Step key={doc.Id} completed={doc.Passed} active={true}>
                                                <StepLabel error={!doc.Passed && (doc.DocId ?? 0) > 0}>{label}</StepLabel>
                                                <StepContent style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>{doc.ErrorMessage}</StepContent>
                                            </Step>;
                                        })}
                                    </Stepper>
                                    : ''}
                            </div>
                        </div>
                    </Grid>
                </Grid>
            </Grid>

            <Dialog open={state.showError} onClose={() => merge({ showError: false })}>
                <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
                <DialogContent><Typography>{state.error}</Typography></DialogContent>
            </Dialog>

            <Dialog maxWidth='xs' fullWidth open={state.showEdit} onClose={() => merge({ showEdit: false })}>
                {state.fields ? <Form
                    onSubmit={generateOrder}
                    initialValues={state.order}
                    render={({ handleSubmit, pristine, invalid, values }: any) => (
                        <form onSubmit={handleSubmit}>
                            <Toolbar className='lbtoolbar'>{'Test Order Options'}</Toolbar>
                            <DialogContent style={{ display: 'flex', flexDirection: 'column' }}>
                                {state.fields!.map(r => <div key={r.section} style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant='h5'>{r.section}</Typography>
                                    {r.fields.map(x => renderField(x.name, x.label, x.options))}
                                </div>)}
                            </DialogContent>
                            <DialogActions>
                                <Button type='submit' color='primary' disabled={invalid || state.disableEdit}>Continue</Button>
                            </DialogActions>
                        </form>)} /> : ''}
            </Dialog>
        </>
    );
};

type TestCasesProps = {
    setTitle?: (title: string, someFlag?: boolean) => void;
};

const TestCases = (props: TestCasesProps) => {
    const { setTitle } = props;
    const [cases, setCases] = useState<TestCaseItem[]>([]);
    const [partner, setPartner] = useState('');
    const [partnerId, setPartnerId] = useState<string | undefined>(undefined);
    const [redirect, setRedirect] = useState(false);
    // partnerId is read inside the async load flow before its state update has
    // flushed, so mirror it in a ref that is always synchronously current.
    const partnerIdRef = useRef<string | undefined>(undefined);
    const disableEdit: boolean | undefined = undefined;

    const loadData = async (testCaseId?: number) => {
        var currentCases = cases;
        if (!testCaseId) {
            currentCases = (await axios.get(Config.api + `/odata/Company/VwTestCaseDetails?$filter=PartnerCoId eq ${partnerIdRef.current}&$orderby=TestName asc`)).data.value;
            setCases(currentCases);
        }

        let saveItems = (i: number, caseObj: TestCaseItem, ret: any) => {
            let docs = ret.data.value;
            caseObj.documents = docs;
            setCases(prev => { const next = [...prev]; next[i] = caseObj; return next; });
        };
        for (var i = 0; i < currentCases.length; i++) {
            let item = currentCases[i];
            if (testCaseId) {
                if (currentCases[i].TestCaseId === testCaseId) {
                    let caseRes = (await axios.get(Config.api + `/odata/Company/VwTestCaseDetails?$filter=PartnerCoId eq ${partnerIdRef.current} and TestCaseId eq ${testCaseId}&$orderby=TestName asc`)).data.value[0];
                    axios.get(Config.api + `/odata/Company/TestCaseDocuments?$filter=ProgressId eq ${caseRes.ProgressId}&$orderby=Id asc`).then(saveItems.bind(null, i, caseRes));
                }
            } else {
                axios.get(Config.api + `/odata/Company/TestCaseDocuments?$filter=ProgressId eq ${item.ProgressId}&$orderby=Id asc`).then(saveItems.bind(null, i, currentCases[i]));
            }

        }
    };

    useEffect(() => {
        (async () => {
            let queryPartnerId = new Helpers().queryString()['partner'];
            partnerIdRef.current = queryPartnerId;
            setPartnerId(queryPartnerId);
            let progress = (await axios.get(Config.api + `/odata/Company/VwTestingProgress?$filter=PartnerCoId eq ${queryPartnerId}`)).data.value[0];
            if (!progress) {
                setRedirect(true);
                return;
            }
            setPartner(progress.PartnerCompanyName);
            if (setTitle) {
                setTitle(`Testing: ${progress.PartnerCompanyName}`, true);
            }
            await loadData();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (redirect) {
        return <Redirect to='/testing' />;
    }
    return (
        <Grid container spacing={2}>
            <Grid size={{ md: 12 }}><Card><Toolbar className='lbtoolbar'>{`Test Cases for ${partner}`}</Toolbar></Card></Grid>
            {cases ? cases.map(item => {
                if (!item.documents) {
                    return (
                        <Grid size={{ md: 12 }} key={item.TestCaseId}>
                            <ContentLoader preserveAspectRatio='none' style={{ height: '25em', width: '100%' }} primaryColor='#e1e1e1' secondaryColor='#d8d8d8' />
                        </Grid>
                    );
                }
                return (
                    <Grid size={{ md: 12 }} key={item.TestCaseId}>
                        <Card>
                            <TestCase item={item} disabled={disableEdit} partnerId={partnerId} onGenerate={() => loadData(item.TestCaseId)} />
                        </Card>
                    </Grid>
                );
            }) : ''}
        </Grid>
    );
};

export default TestCases;
export { TestCase };
